"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type ToastMessage = {
  id: number;
  message: string;
};

type ToastContextValue = {
  showToast: (message: string, durationMs?: number) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const nextToastIdRef = useRef(1);
  const timeoutIdsRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const removeToast = useCallback((id: number) => {
    setMessages((previous) => previous.filter((item) => item.id !== id));
    const timeoutId = timeoutIdsRef.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutIdsRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (message: string, durationMs = 2_000) => {
      const trimmedMessage = message.trim();
      if (!trimmedMessage) return;

      const id = nextToastIdRef.current++;
      setMessages((previous) => [...previous, { id, message: trimmedMessage }]);

      const timeoutId = setTimeout(() => {
        removeToast(id);
      }, durationMs);
      timeoutIdsRef.current.set(id, timeoutId);
    },
    [removeToast]
  );

  useEffect(() => {
    return () => {
      for (const timeoutId of timeoutIdsRef.current.values()) {
        clearTimeout(timeoutId);
      }
      timeoutIdsRef.current.clear();
    };
  }, []);

  const contextValue = useMemo<ToastContextValue>(
    () => ({
      showToast,
    }),
    [showToast]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {messages.length > 0 ? (
        <div
          aria-live="polite"
          className="pointer-events-none fixed inset-x-0 bottom-4 z-[120] flex justify-center px-4 sm:inset-auto sm:right-4 sm:top-4 sm:w-auto sm:justify-end"
        >
          <div className="flex w-full max-w-sm flex-col gap-2 sm:w-[22rem]">
            {messages.map((item) => (
              <div
                key={item.id}
                className="rounded-xl border border-[#d4c7af] bg-[#fffaf1] px-4 py-2.5 text-sm text-[#334740] shadow-[0_10px_24px_rgba(34,47,42,0.14)]"
              >
                {item.message}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) {
    throw new Error("useToast must be used within ToastProvider.");
  }
  return value;
}
